import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Table, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch, faEye } from "@fortawesome/free-solid-svg-icons";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";

export const LoadPromptModal = ({ show, onHide, onPromptLoaded }) => {
  const [prompts, setPrompts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMyPromptsOnly, setShowMyPromptsOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [error, setError] = useState(null);
  const [okoUser, setOkoUser] = useState(null);

  useEffect(() => {
    if (show) {
      fetchPrompts();
      fetchOkoUser();
    }
  }, [show]);

  const fetchPrompts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await request("explanation/prompt/summary", {
        method: "GET"
      });
      setPrompts(response);
    } catch (e) {
      logError("Admin prompt operation failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "An unknown error occurred while fetching prompts.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOkoUser = async () => {
    try {
      const response = await request("account/oko_user", {
        method: "GET"
      });
      setOkoUser(response);
    } catch (e) {
      console.error("Error fetching oko user:", e);
      logError("Admin prompt operation failed", e, { category: LogCategory.ERROR });
    }
  };

  // Calculate filtered prompts directly instead of using state
  const filteredPrompts = prompts.filter(prompt => {
    // Filter by search term
    const matchesSearch = !searchTerm ||
      prompt.name.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by current user
    const matchesUser = !showMyPromptsOnly ||
      (okoUser && prompt.updated_by === okoUser.id);

    return matchesSearch && matchesUser;
  });

  const handleLoadPrompt = async (promptId) => {
    setIsLoadingPrompt(true);
    setError(null);

    try {
      const response = await request(`explanation/prompt/get?prompt_id=${promptId}`, {
        method: "GET"
      });
      onPromptLoaded(response);
    } catch (e) {
      console.error("Error loading prompt:", e);
      logError("Admin prompt operation failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "An unknown error occurred while loading the prompt.");
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const handleClose = () => {
    // Don't reset search and filter states - let user keep their preferences
    setError(null);
    onHide();
  };

  const handleReset = () => {
    setSearchTerm("");
    setShowMyPromptsOnly(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Load Explanation Prompt</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="mb-3">
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search prompts by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showMyPromptsOnly"
              checked={showMyPromptsOnly}
              onChange={(e) => setShowMyPromptsOnly(e.target.checked)}
              disabled={!okoUser}
            />
            <label className="form-check-label" htmlFor="showMyPromptsOnly">
              Show only my prompts {showMyPromptsOnly ? '✓' : '○'}
              {!okoUser && <span className="text-muted"> (loading user info...)</span>}
            </label>
          </div>

          <div className="mt-2 text-muted small">
            Showing {filteredPrompts.length} of {prompts.length} prompts
            {searchTerm && ` (search: "${searchTerm}")`}
            {showMyPromptsOnly && ` (my prompts only)`}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <div className="mt-2">Loading prompts...</div>
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Updated By</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrompts.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      {prompts.length === 0 ? "No prompts found." : "No prompts match your search criteria."}
                    </td>
                  </tr>
                ) : (
                  filteredPrompts.map((prompt) => (
                    <tr key={prompt.id}>
                      <td>{prompt.name}</td>
                      <td>{prompt.updated_by}</td>
                      <td>{formatDate(prompt.updated_at)}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleLoadPrompt(prompt.id)}
                          disabled={isLoadingPrompt}
                        >
                          {isLoadingPrompt ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faEye} className="mr-1" />
                              &nbsp;Load
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex justify-content-between mt-3">
          <Button variant="outline-secondary" onClick={handleReset}>
            Clear Filters
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
